/**
 * /admin/agents/rag — Global Admin RAG inspector (#601).
 *
 * Side-by-side comparison of all four hint retrievers (top-K, dense,
 * lexical, hybrid) for a single (variant, agent, query) tuple.
 * Bypasses HINT_RETRIEVAL_MODE so the inspector still works in prod
 * where mode=topk would skip RAG entirely.
 *
 * Layout (#692): AgentsSubNav + header, then a two-column top half —
 * LEFT "From production" (a Load button that fills an inline list of
 * recent agent queries; picking one fills the form) and RIGHT "Query"
 * (the form that gets run) — then the four-column results below.
 *
 * Read-only — no writes other than the server-side audit row each
 * inspector query emits.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Input, Label } from '@/components/ui'
import {
  adminRagAPI,
  type RagAgentKind,
  type RagConfigResponse,
  type RagHitDisplay,
  type RagInspectorResponse,
  type RagSampleQuery,
} from '@/lib/api'
import { AgentsSubNav } from '../AgentsSubNav'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'

const K_DEFAULT = 10
const K_MAX = 50

export function RagInspectorPage() {
  const tz = useUserTimezone()
  const [variantId, setVariantId] = useState('')
  const [agentKind, setAgentKind] = useState<RagAgentKind>('generator')
  const [queryText, setQueryText] = useState('')
  const [k, setK] = useState<number>(K_DEFAULT)

  const [result, setResult] = useState<RagInspectorResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [samples, setSamples] = useState<RagSampleQuery[]>([])
  const [samplesLoading, setSamplesLoading] = useState(false)
  // Whether the operator has asked to see the production-query list.
  // Flips true on the first "Load from production" click; the list
  // then stays visible in the left column (re-clicking refreshes it).
  const [prodListShown, setProdListShown] = useState(false)
  // The prod-query row currently applied to the form — kept so the
  // left-column list can highlight it. Set by `applySample`, and
  // cleared the moment the operator edits query text / variant id /
  // agent, so the highlight never outlives a form that no longer
  // matches the sample.
  const [selectedSampleId, setSelectedSampleId] = useState<string | null>(null)
  // The variant-id scope the prod-query list was last fetched for
  // ('' = all variants). Drives the list's scope label so it reflects
  // what was actually loaded — not the live `variantId` field, which
  // can drift ahead of the list once the operator edits it.
  const [loadedScope, setLoadedScope] = useState('')

  // Live status of what production is actually doing — fetched on
  // mount and refreshed after every Run so an operator who rotates
  // env mid-session sees the update. Replaces the hardcoded "production
  // serves top-K" claim that used to live in the page header (#607).
  //
  // On fetch failure we deliberately KEEP the prior `config` value
  // instead of clearing to null (Copilot review on #611) — a transient
  // network/auth blip between Runs would otherwise yank the chip away
  // even though the last known value is still the best signal we have.
  const [config, setConfig] = useState<RagConfigResponse | null>(null)
  const reloadConfig = useCallback(async () => {
    try {
      setConfig(await adminRagAPI.getConfig())
    } catch (e) {
      console.warn('[RAG inspector] config load failed; keeping prior value', e)
    }
  }, [])
  useEffect(() => {
    void reloadConfig()
  }, [reloadConfig])

  const loadSamples = useCallback(async () => {
    setSamplesLoading(true)
    // Capture the scope up front so the label reflects what THIS fetch
    // was scoped to, even if `variantId` changes while it's in flight.
    const scope = variantId.trim()
    try {
      // If a variant id is already typed, scope samples to it; otherwise
      // show recent activity across variants so the operator can pick
      // from anywhere.
      const rows = await adminRagAPI.sampleQueries({
        variantId: scope || undefined,
        limit: 20,
      })
      setSamples(rows)
      setLoadedScope(scope)
    } catch (e) {
      // The list is non-critical; if it fails just log it. The form
      // still works without samples.
      console.warn('[RAG inspector] sample-query load failed', e)
    } finally {
      setSamplesLoading(false)
    }
  }, [variantId])

  // Auto-load samples the first time the list is shown.
  useEffect(() => {
    if (prodListShown && samples.length === 0 && !samplesLoading) {
      void loadSamples()
    }
  }, [prodListShown, samples.length, samplesLoading, loadSamples])

  // "Load from production" — first click reveals the list (the effect
  // above fetches it); a later click re-fetches so the list reflects
  // the current variant-id scope.
  function handleLoadFromProduction(): void {
    if (!prodListShown) {
      setProdListShown(true)
    } else {
      void loadSamples()
    }
  }

  function applySample(s: RagSampleQuery): void {
    setVariantId(s.variantId)
    setAgentKind(s.agentPhase)
    setQueryText(s.queryText)
    setSelectedSampleId(s.id)
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await adminRagAPI.query({
        variantId: variantId.trim(),
        agentKind,
        queryText: queryText.trim(),
        k,
      })
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run inspector query')
      setResult(null)
    } finally {
      setLoading(false)
      // Refresh the status chip after every Run so an operator who
      // rotated `HINT_RETRIEVAL_MODE` / `HINT_RETRIEVER` mid-session
      // sees the new state without a full page reload.
      void reloadConfig()
    }
  }

  const canSubmit = variantId.trim().length > 0 && queryText.trim().length > 0 && !loading

  return (
    <div className="space-y-6 max-w-[1400px]">
      <AgentsSubNav />

      <header className="space-y-2">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-semibold">Agent Memory inspector</h1>
          <LiveConfigChip config={config} />
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Compare what each hint retriever would return for a given query against today's hint
          index. Deliberately bypasses{' '}
          <code className="bg-muted px-1 py-0.5 rounded text-xs">HINT_RETRIEVAL_MODE</code> so all
          four columns run regardless of which mode production happens to be in — the chip above
          shows what production is actually serving right now.
        </p>
      </header>

      {/* Top half — two columns: "From production" (left), "Query" (right). */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">

        {/* LEFT — From production. The Load button fills the list inline
            below it, in this same column; picking a row fills the form. */}
        <section className="lg:w-[42%] border rounded-md flex flex-col min-w-0">
          <div className="px-4 py-2.5 border-b flex items-center justify-between gap-2">
            <span className="text-sm font-medium">From production</span>
            {prodListShown && !samplesLoading && samples.length > 0 && (
              <span className="text-xs text-muted-foreground">{samples.length} recent</span>
            )}
          </div>
          <div className="p-3 flex flex-col gap-3 flex-1 min-h-0">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleLoadFromProduction}
            >
              {prodListShown ? 'Reload from production' : 'Load from production'}
            </Button>

            {!prodListShown ? (
              <div className="flex-1 flex items-center justify-center rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground italic">
                Browse recent queries an agent actually emitted, then pick one to fill the form —
                or just type a query on the right.
              </div>
            ) : (
              <div className="border rounded-md flex flex-col flex-1 min-h-0 overflow-hidden">
                <div className="px-3 py-1.5 text-xs text-muted-foreground border-b">
                  scoped to{' '}
                  {loadedScope ? (
                    <code className="bg-muted px-1 rounded">{loadedScope}</code>
                  ) : (
                    'all variants'
                  )}
                  {variantId.trim() !== loadedScope && (
                    <span className="ml-1 text-amber-500">
                      · Variant ID changed — Reload to rescope
                    </span>
                  )}
                </div>
                {samplesLoading ? (
                  <p className="px-3 py-6 text-sm text-muted-foreground">Loading recent queries…</p>
                ) : samples.length === 0 ? (
                  <p className="px-3 py-6 text-sm text-muted-foreground italic">
                    No recent retrievals match the current filter.
                  </p>
                ) : (
                  <ul className="overflow-y-auto max-h-[340px] divide-y">
                    {samples.map((s) => {
                      const selected = s.id === selectedSampleId
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => applySample(s)}
                            aria-pressed={selected}
                            className={
                              'w-full text-left px-3 py-2 border-l-2 transition-colors ' +
                              (selected
                                ? 'border-primary bg-primary/10'
                                : 'border-transparent hover:bg-muted/50')
                            }
                          >
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{formatLocalDateTime(s.createdAt, tz)}</span>
                              <span>·</span>
                              <span className="font-mono">{s.agentPhase}</span>
                              <span>·</span>
                              <span className="font-mono truncate" title={s.variantId}>
                                {s.variantId.slice(0, 18)}
                              </span>
                              <span className="ml-auto whitespace-nowrap">
                                rag:{s.ragHitCount} · topk:{s.topkHitCount}
                              </span>
                            </div>
                            <div className="text-sm mt-0.5 line-clamp-2">{s.queryText}</div>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </section>

        {/* RIGHT — Query. The form the operator runs; filled either by
            hand or by picking a row from the left column. */}
        <form onSubmit={onSubmit} className="lg:flex-1 border rounded-md flex flex-col min-w-0">
          <div className="px-4 py-2.5 border-b">
            <span className="text-sm font-medium">Query</span>
          </div>
          <div className="p-4 flex flex-col gap-4 flex-1">
            <div className="space-y-1.5">
              <Label htmlFor="rag-query">Query text</Label>
              <textarea
                id="rag-query"
                value={queryText}
                onChange={(e) => {
                  setQueryText(e.target.value)
                  setSelectedSampleId(null)
                }}
                placeholder="What the agent would ask the retriever — a failure message, step description, or natural-language intent."
                className="w-full min-h-[88px] border rounded-md px-3 py-2 text-sm font-sans bg-background"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rag-variant-id">Variant ID</Label>
              <Input
                id="rag-variant-id"
                value={variantId}
                onChange={(e) => {
                  setVariantId(e.target.value)
                  setSelectedSampleId(null)
                }}
                placeholder="paste a Variant _id"
                className="font-mono"
              />
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-4">
              <div className="space-y-1.5">
                <Label>Agent</Label>
                <div className="inline-flex rounded-md border p-0.5 bg-background w-fit">
                  {(['generator', 'healer'] as const).map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      className={
                        'text-sm px-3 py-1 rounded-sm transition-colors capitalize ' +
                        (agentKind === kind
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted')
                      }
                      onClick={() => {
                        setAgentKind(kind)
                        setSelectedSampleId(null)
                      }}
                      aria-pressed={agentKind === kind}
                    >
                      {kind}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rag-k">Hits / column</Label>
                <Input
                  id="rag-k"
                  type="number"
                  min={1}
                  max={K_MAX}
                  value={k}
                  onChange={(e) => setK(Number(e.target.value) || K_DEFAULT)}
                  className="w-20"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center gap-3 mt-auto pt-1">
              <Button type="submit" disabled={!canSubmit}>
                {loading ? 'Running…' : 'Run'}
              </Button>
              {result && (
                <p className="text-xs text-muted-foreground">
                  Expanded query:{' '}
                  <code className="bg-muted px-1 rounded">{result.expandedQuery}</code>
                </p>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Results — four columns, below the two-column top half. */}
      {result && (
        <section className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Variant:{' '}
            <span className="text-foreground">
              {result.variant.workspaceName ?? '(unknown workspace)'} ›{' '}
              {result.variant.applicationName ?? '(unknown app)'} › {result.variant.name}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <ResultColumn
              title="Top-K"
              code="TK"
              subtitle="popularity · confirmationCount desc"
              accent="amber"
              queryBlind
              hits={result.results.topk}
            />
            <ResultColumn
              title="Dense"
              code="DV"
              subtitle="semantic vector · cosine"
              accent="blue"
              hits={result.results.dense}
            />
            <ResultColumn
              title="Lexical"
              code="LX"
              subtitle="BM25 · MongoDB $text"
              accent="green"
              hits={result.results.lexical}
            />
            <ResultColumn
              title="Hybrid"
              code="HY"
              subtitle="RRF · dense + lexical"
              accent="violet"
              hits={result.results.hybrid}
              showContributions
            />
          </div>
        </section>
      )}
    </div>
  )
}

interface ResultColumnProps {
  title: string
  /** Explicit two-letter code rendered in the column badge. Mirrors
   * the codes the mockup uses (TK/DV/LX/HY) — derived once-and-fixed
   * instead of slicing the title so "Top-K" doesn't render as "TO". */
  code: string
  subtitle: string
  accent: 'amber' | 'blue' | 'green' | 'violet'
  hits: RagHitDisplay[]
  queryBlind?: boolean
  showContributions?: boolean
}

/** Live status chip — renders the resolved `HINT_RETRIEVAL_MODE` +
 * `HINT_RETRIEVER` so the page surface always reflects what production
 * is actually doing instead of stale hardcoded copy (#607). Three
 * states: loading (no chip yet), known (mode + retriever), errored
 * (gracefully omitted — surfaced only via console.warn since the
 * inspector itself still works without the chip). */
function LiveConfigChip({ config }: { config: RagConfigResponse | null }) {
  if (!config) return null
  // Only `rag` actually INJECTS via the retriever. `dual` runs the
  // retriever for comparison but still injects the top-K (popularity)
  // set — so it is NOT live, despite exercising the retriever. `topk`
  // doesn't run the retriever at all. Distinguish all three honestly so
  // the chip can't read as "hybrid is live" when it isn't (#1654).
  const live = config.mode === 'rag'
  const shadow = config.mode === 'dual'
  const modeAccent = live
    ? 'bg-violet-500/10 text-violet-400 border-violet-500/30'
    : 'bg-amber-500/10 text-amber-500 border-amber-500/30'
  return (
    <div
      className="flex items-center gap-1 text-xs"
      title={
        live
          ? `Live — hints are selected by relevance via the ${config.retriever} retriever.`
          : shadow
            ? `Shadow only — the ${config.retriever} retriever runs for comparison but hints are still injected by top-K (popularity). Not live.`
            : `Top-K (popularity) only — the ${config.retriever} retriever is configured but does not run.`
      }
    >
      <span className="text-muted-foreground">production:</span>
      <span className={`px-1.5 py-0.5 rounded font-mono border ${modeAccent}`}>
        mode={config.mode}
      </span>
      <span className="text-muted-foreground">·</span>
      <span
        className={
          'px-1.5 py-0.5 rounded font-mono border ' +
          (live
            ? 'bg-blue-500/10 text-blue-500 border-blue-500/30'
            : 'bg-muted text-muted-foreground border-transparent')
        }
      >
        retriever={config.retriever}
      </span>
    </div>
  )
}

function ResultColumn({
  title,
  code,
  subtitle,
  accent,
  hits,
  queryBlind,
  showContributions,
}: ResultColumnProps) {
  // Tailwind doesn't see dynamically-composed class names; map accent
  // to literal strings the compiler can scan.
  const accentBg =
    accent === 'amber'
      ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
      : accent === 'blue'
        ? 'bg-blue-500/10 text-blue-500 border-blue-500/30'
        : accent === 'green'
          ? 'bg-green-500/10 text-green-500 border-green-500/30'
          : 'bg-violet-500/10 text-violet-400 border-violet-500/30'

  return (
    <div className="border rounded-md flex flex-col min-h-[200px]">
      <div className="px-3 py-2.5 border-b">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono border ${accentBg}`}>
            {code}
          </span>
          <span>{title}</span>
          {queryBlind && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/30 cursor-help"
              title="Top-K ranks by confirmationCount; the query text is NOT used for this column"
            >
              ⓘ ignores query
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
      </div>
      <div className="p-2 flex-1 overflow-y-auto max-h-[640px] space-y-2">
        {hits.length === 0 ? (
          <p className="px-2 py-6 text-xs text-muted-foreground italic text-center">No hits.</p>
        ) : (
          hits.map((h) => <HitCard key={h.hintId} hit={h} showContributions={showContributions} />)
        )}
      </div>
    </div>
  )
}

function HitCard({
  hit,
  showContributions,
}: {
  hit: RagHitDisplay
  showContributions?: boolean
}) {
  // Format the score appropriately for each label. Top-K's
  // `confirms` is an integer; the others are floats. RRF scores are
  // tiny (e.g. 0.031), cosine is [0, 1], textScore can be > 1.
  const scoreStr =
    hit.scoreLabel === 'confirms'
      ? String(hit.scoreValue)
      : hit.scoreLabel === 'rrf'
        ? hit.scoreValue.toFixed(4)
        : hit.scoreValue.toFixed(2)

  return (
    <div className="border rounded-md p-2.5 space-y-1.5 bg-card hover:border-muted-foreground/30 transition-colors">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground font-mono">#{hit.rank}</span>
        <span className="font-mono">
          <span className="text-muted-foreground">{hit.scoreLabel}:</span>{' '}
          <span className="text-foreground font-semibold">{scoreStr}</span>
        </span>
      </div>
      {hit.pattern && <div className="text-sm leading-snug">{hit.pattern}</div>}
      {hit.antipattern && (
        <div className="text-xs">
          <span className="text-muted-foreground">antipattern:</span>{' '}
          <span className="text-destructive">{hit.antipattern}</span>
        </div>
      )}
      {(hit.category || hit.element || hit.selector) && (
        <div className="text-xs text-muted-foreground space-x-2">
          {hit.category && (
            <span>
              <span className="opacity-70">category:</span> {hit.category}
            </span>
          )}
          {hit.element && (
            <span>
              <span className="opacity-70">element:</span> {hit.element}
            </span>
          )}
        </div>
      )}
      {showContributions && (
        <div className="flex gap-1.5 pt-1 border-t">
          <Contribution kind="dense" entry={hit.denseContribution} />
          <Contribution kind="lexical" entry={hit.lexicalContribution} />
        </div>
      )}
      {hit.sourceRunId && (
        <div className="text-[10px] text-muted-foreground font-mono">
          source: {hit.sourceRunId}
        </div>
      )}
    </div>
  )
}

function Contribution({
  kind,
  entry,
}: {
  kind: 'dense' | 'lexical'
  entry?: { score?: number; rank?: number }
}) {
  const label = kind === 'dense' ? 'DV' : 'LX'
  const accent =
    kind === 'dense'
      ? 'bg-blue-500/10 text-blue-500'
      : 'bg-green-500/10 text-green-500'
  // Missing entirely (retriever didn't surface this hit) — show "—".
  if (!entry || entry.score === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground">
        <span className="opacity-70">{label}</span>—
      </span>
    )
  }
  const scoreStr = kind === 'dense' ? entry.score.toFixed(2) : entry.score.toFixed(1)
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${accent}`}>
      <span className="opacity-70">{label}</span>
      {scoreStr}
      {entry.rank !== undefined && (
        <span className="opacity-70"> · #{entry.rank}</span>
      )}
    </span>
  )
}
